import { useState } from "react";
import { Link } from "react-router-dom";

/**
 * Renders a brand-logo grid or strip used on the homepage and service
 * pages. Never claims partnership / certification / exclusivity —
 * simply shows product names in use at the clinic.
 *
 * Each brand cell picks between:
 *   • the real `<img>` if `logoSrc` is present (SVG or transparent PNG),
 *   • otherwise a text wordmark in the site's display font.
 *
 * Layout:
 *   variant="homepage" → 2 cols mobile, 4 cols desktop grid.
 *   variant="strip"    → single flex row, wraps on small screens.
 */
export default function BrandLogos({
  brands,
  variant = "homepage",
  supportingText = null,
}) {
  if (!brands || brands.length === 0) return null;

  const cellHeight = variant === "homepage" ? "h-24 md:h-28" : "h-16";
  const gridCls =
    variant === "homepage"
      ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6"
      : "flex flex-wrap items-center gap-4 md:gap-5";

  return (
    <div className={variant === "homepage" ? "mt-10" : "mt-6"} data-testid={`brand-logos-${variant}`}>
      <div className={gridCls}>
        {brands.map((b) => (
          <BrandCell key={b.id} brand={b} cellHeight={cellHeight} variant={variant} />
        ))}
      </div>
      {supportingText && (
        <p className="mt-4 text-sm text-slate-600 leading-relaxed">{supportingText}</p>
      )}
    </div>
  );
}

function BrandCell({ brand, cellHeight, variant }) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = brand.logoSrc && !imgFailed;
  const inner = (
    <div
      className={`bg-white border border-slate-200 rounded-2xl ${cellHeight} ${variant === "strip" ? "px-5 min-w-[140px]" : "px-4"} flex items-center justify-center transition-shadow duration-200 hover:shadow-[0_6px_18px_-8px_rgb(15_44_89_/_0.18)] hover:border-slate-300`}
      data-testid={`brand-cell-${brand.id}`}
    >
      {showImage ? (
        <img
          src={brand.logoSrc}
          alt={brand.name}
          width={brand.logoW || undefined}
          height={brand.logoH || undefined}
          className="max-h-10 md:max-h-12 w-auto object-contain"
          loading="lazy"
          decoding="async"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <Wordmark name={brand.name} style={brand.wordmarkStyle} />
      )}
    </div>
  );

  if (brand.href) {
    return (
      <Link
        to={brand.href}
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F2C59] focus-visible:ring-offset-2 rounded-2xl"
        aria-label={`${brand.name} — tratamente Denti Service`}
      >
        {inner}
      </Link>
    );
  }
  return inner;
}

// Text-only fallback rendered until the real press-kit asset is placed
// under /app/frontend/public/logos/. Uses the site's display font so it
// blends with the rest of the identity. Never a copy of the real mark.
function Wordmark({ name, style = "sans-strong" }) {
  const base = "font-display tracking-tight text-[#0F2C59] leading-none text-center select-none";
  const styles = {
    "sans-strong": "font-extrabold text-lg md:text-xl uppercase",
    serif: "font-bold text-lg md:text-xl italic",
  };
  return <span className={`${base} ${styles[style] || styles["sans-strong"]}`}>{name}</span>;
}
